import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { useClientConfigQuery } from "api";
import {
  ActionTypes,
  ConfigStatusType,
  useConfig,
} from "contexts/ConfigContext";
import useFavicon from "hooks/useFavicon";
import useQueryParams from "hooks/useQueryParams";
import { useEffect, useMemo } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";

import ErrorFallback from "components/ErrorFallback";
import Loading from "components/Loading";
import { ClientParams, routes } from "config/urls";
import NoMatch from "pages/NoMatch";

import ClientContainer from "./ClientContainer";
import CreateCompany from "./CreateCompany";

export function Client({ match }: RouteComponentProps<ClientParams>) {
  const { state, dispatch } = useConfig();
  const analytics = useAnalytics();

  const queryParams = useQueryParams();

  const [{ data, loading, error }] = useClientConfigQuery({
    clientId: match.params.clientId,
    queryParams,
  });

  useFavicon(data?.icon?.src);

  const userProperties = useMemo(
    () => ({
      clientId: data?.clientId,
      clientName: data?.clientName,
    }),
    [data?.clientId, data?.clientName]
  );

  useEffect(() => {
    if (userProperties.clientId && userProperties.clientName) {
      analytics.setUserProperties(userProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProperties]);

  useEffect(() => {
    if (data) {
      dispatch({
        type: ActionTypes.SET_CONFIG,
        payload: data,
      });
    }
  }, [data, dispatch]);

  if (error) {
    return <ErrorFallback error={error} />;
  }

  if (loading || state.status === ConfigStatusType.EMPTY) {
    return <Loading dataTestId="client-config-loading" />;
  }

  return (
    <EventContextProvider data={userProperties}>
      <Switch>
        <Route
          exact
          path={routes.client.createCompany}
          component={CreateCompany}
        />
        <Route exact path={routes.client.root} component={ClientContainer} />
        <Route path="*" component={NoMatch} />
      </Switch>
    </EventContextProvider>
  );
}
